import React, { useState, useEffect } from "react";
import {
	XMarkIcon,
	PlusIcon,
	MinusIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useTicketStore } from "../../../store/ticketStore";
import { parseSeatRange } from "../../../utils/seatRange";

const RegisterDetailWithImageModal = ({ isOpen, onClose }) => {
	// 초기 상태 값 정의
	const initialTicketItem = {
		reservationNumber: "",
		departureDate: new Date().toISOString().split("T")[0],
		departureTime: new Date().setHours(new Date().getHours() - 9),
		arrivalTime: new Date().setHours(new Date().getHours() - 9),
		trainCode: "",
		departureStation: "",
		arrivalStation: "",
		seatInfoList: [
			{
				trainCarNumber: "",
				seatCode: "",
			},
		],
	};

	// 상태 관리
	const [ticketItems, setTicketItems] = useState([
		{
			reservationNumber: "",
			departureDate: new Date().toISOString().split("T")[0],
			departureTime: new Date().setHours(new Date().getHours() - 9),
			arrivalTime: new Date().setHours(new Date().getHours() - 9),
			trainCode: "",
			departureStation: "",
			arrivalStation: "",
			seatInfoList: [
				{
					trainCarNumber: "",
					seatCode: "",
				},
			],
		},
	]);
	
	// 티켓 타입 드롭다운 상태 관리
	const [isTicketTypeDropdownOpen, setIsTicketTypeDropdownOpen] = useState(false);
	const [selectedTicketIndex, setSelectedTicketIndex] = useState(0); // 선택된 열차 인덱스
	const [isSaving, setIsSaving] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [ticketType, setTicketType] = useState("일반승차권");
	const { saveTicketDataWithImage } = useTicketStore();

	// 모달이 열리거나 닫힐 때 상태 초기화
	useEffect(() => {
		// 모달이 닫힐 때 모든 데이터 초기화
		if (!isOpen) {
			setTicketItems([{ ...initialTicketItem }]);
			setSelectedImage(null);
			setImagePreview(null);
			setTicketType("일반승차권");
			setIsSaving(false);
			setIsTicketTypeDropdownOpen(false);
		}
	}, [isOpen]);

	// 모달이 닫혀있으면 렌더링하지 않음
	if (!isOpen) return null;

	// 좌석 추가 핸들러
	const handleAddSeat = (ticketIndex) => {
		const updatedItems = [...ticketItems];
		if (!updatedItems[ticketIndex].seatInfoList) {
			updatedItems[ticketIndex].seatInfoList = [];
		}

		updatedItems[ticketIndex].seatInfoList.push({
			trainCarNumber: "",
			seatCode: "",
		});

		setTicketItems(updatedItems);
	};

	// 좌석 삭제 핸들러
	const handleRemoveSeat = (ticketIndex, seatIndex) => {
		const updatedItems = [...ticketItems];
		updatedItems[ticketIndex].seatInfoList = updatedItems[
			ticketIndex
		].seatInfoList.filter((_, idx) => idx !== seatIndex);

		setTicketItems(updatedItems);
	};

	// 이미지 업로드 핸들러
	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		// 파일 형식 검사
		const validTypes = ["image/jpeg", "image/png", "image/jpg"];
		if (!validTypes.includes(file.type)) {
			alert("이미지 파일(JPG, PNG)만 업로드 가능합니다.");
			return;
		}

		// 파일 크기 검사 (10MB 제한)
		if (file.size > 10 * 1024 * 1024) {
			alert("파일 크기가 10MB를 초과합니다.");
			return;
		}

		setSelectedImage(file);

		// 이미지 미리보기 생성
		const reader = new FileReader();
		reader.onload = () => {
			setImagePreview(reader.result);
		};
		reader.readAsDataURL(file);
	};

	// 티켓 타입 변경 핸들러 - 모든 티켓에 일괄 적용
	const handleTicketTypeChange = (e) => {
		setTicketType(e.target.value);
		
		// 티켓 타입은 저장 시 모든 티켓에 적용되므로 여기서는 별도의 추가 작업이 필요하지 않습니다.
	};

	// 열차 삭제 핸들러
	const handleRemoveTicket = (ticketIndex) => {
		// 마지막 열차는 삭제할 수 없음
		if (ticketItems.length <= 1) {
			alert("최소 한 개의 열차는 유지해야 합니다.");
			return;
		}

		// 삭제 확인
		if (!window.confirm("정말 이 열차를 삭제하시겠습니까?")) {
			return;
		}

		const updatedItems = ticketItems.filter(
			(_, index) => index !== ticketIndex
		);

		// 선택된 인덱스 조정
		if (selectedTicketIndex >= ticketIndex) {
			// 현재 선택된 열차가 삭제되거나 그 이후의 열차인 경우 인덱스 조정
			const newIndex = Math.max(0, selectedTicketIndex - 1);
			setSelectedTicketIndex(newIndex);
		}

		setTicketItems(updatedItems);
	};

	// 열차 추가 핸들러
	const handleAddTicket = () => {
		const newTicket = {
			reservationNumber: "",
			departureDate: new Date().toISOString().split("T")[0],
			departureTime: new Date().setHours(new Date().getHours() - 9),
			arrivalTime: new Date().setHours(new Date().getHours() - 9),
			trainCode: "",
			departureStation: "",
			arrivalStation: "",
			seatInfoList: [
				{
					trainCarNumber: "",
					seatCode: "",
				},
			],
		};
		setTicketItems([...ticketItems, newTicket]);

		// 새로 추가된 열차를 자동으로 선택
		setSelectedTicketIndex(ticketItems.length);
	};

	// 티켓 정보 업데이트 핸들러
	const handleTicketChange = (ticketIndex, field, value) => {
		const updatedItems = [...ticketItems];
		updatedItems[ticketIndex][field] = value;
		setTicketItems(updatedItems);
	};

	// 좌석 정보 업데이트 핸들러
	const handleSeatChange = (ticketIndex, seatIndex, field, value) => {
		const updatedItems = [...ticketItems];
		updatedItems[ticketIndex].seatInfoList[seatIndex][field] = value;
		setTicketItems(updatedItems);
	};

	const applySeatRange = (ticketIndex, seatIndex, seatCode) => {
		const result = parseSeatRange(seatCode);
		if (!result.isRange || result.error || result.seats.length === 0) {
			return false;
		}

		const updatedItems = [...ticketItems];
		const seatInfoList = [
			...(updatedItems[ticketIndex].seatInfoList || []),
		];
		const baseSeat = {
			trainCarNumber: seatInfoList[seatIndex]?.trainCarNumber ?? "",
		};

		seatInfoList[seatIndex] = {
			...baseSeat,
			seatCode: result.seats[0],
		};

		const additionalSeats = result.seats.slice(1).map((code) => ({
			...baseSeat,
			seatCode: code,
		}));

		seatInfoList.splice(seatIndex + 1, 0, ...additionalSeats);
		updatedItems[ticketIndex].seatInfoList = seatInfoList;
		setTicketItems(updatedItems);
		return true;
	};

	// 저장 핸들러
	const handleSave = async () => {
		try {
			// 이미지 필수 검사
			if (!selectedImage) {
				alert("승차권 이미지를 업로드해주세요.");
				return;
			}

			// 티켓 정보 검사
			if (ticketItems.length === 0) {
				alert("최소 하나의 티켓 정보를 입력해주세요.");
				return;
			}

			// 빈 값이 없는지 검사
			const requiredFields = [
				{ field: 'reservationNumber', name: '예약번호' },
				{ field: 'trainCode', name: '열차번호' },
				{ field: 'departureStation', name: '출발역' },
				{ field: 'arrivalStation', name: '도착역' },
				{ field: 'departureDate', name: '출발일자' },
				{ field: 'departureTime', name: '출발시간' },
				{ field: 'arrivalTime', name: '도착시간' }
			];
			
			for (let i = 0; i < ticketItems.length; i++) {
				const ticket = ticketItems[i];
				
				// 필수 필드 검사
				for (const { field, name } of requiredFields) {
					if (!ticket[field]) {
						alert(`승차권 #${i + 1}의 ${name} 정보가 비어있습니다.`);
						return;
					}
				}
				
				// 좌석 정보 검사
				if (ticket.seatInfoList.length === 0) {
					alert(`승차권 #${i + 1}의 좌석 정보가 없습니다.`);
					return;
				}
				
				// 각 좌석의 필수 필드 검사
				for (let j = 0; j < ticket.seatInfoList.length; j++) {
					const seat = ticket.seatInfoList[j];
					if (!seat.trainCarNumber) {
						alert(`승차권 #${i + 1}의 좌석 #${j + 1}의 호차 번호가 비어있습니다.`);
						return;
					}
					if (!seat.seatCode) {
						alert(`승차권 #${i + 1}의 좌석 #${j + 1}의 좌석 번호가 비어있습니다.`);
						return;
					}
				}
			}

			setIsSaving(true);

			// 폼 데이터 생성
			const formData = new FormData();

			// 티켓 타입 추가
			formData.append("ticket_image", selectedImage);

			// 모든 티켓 정보를 배열로 변환
			const formattedTickets = ticketItems.map(ticket => {
				return {
					ticket_type: ticketType,
					train_code: ticket.trainCode,
					reservation_number: ticket.reservationNumber,
					departure_station: ticket.departureStation || "-",
					arrival_station: ticket.arrivalStation || "-",
					departure_date: ticket.departureDate,
					departure_time: (() => {
						if (!ticket.departureTime) return "";
						const depTime = new Date(ticket.departureTime);
						depTime.setHours(depTime.getHours() + 9);
						return depTime.toTimeString().slice(0, 5);
					})(),
					arrival_time: (() => {
						if (!ticket.arrivalTime) return "";
						const arrTime = new Date(ticket.arrivalTime);
						arrTime.setHours(arrTime.getHours() + 9);
						return arrTime.toTimeString().slice(0, 5);
					})(),
					seat_info_list: ticket.seatInfoList.map((seat) => ({
						seat_code: `${seat.trainCarNumber} ${seat.seatCode}`,
					})),
				};
			});

			// 폼데이터에 티켓 정보 배열 추가
			formData.append("data", JSON.stringify(formattedTickets));

			// API 호출
			const results = await saveTicketDataWithImage(formData);

			// 결과 확인
			const allSuccess = results.every((result) => result.success);
			if (allSuccess) {
				alert("티켓 정보가 성공적으로 저장되었습니다.");
				onClose(true);
			} else {
				alert(results[0].error || "저장에 실패했습니다.");
				console.error(
					"저장 실패:",
					results.filter((r) => !r.success)
				);
			}
		} catch (error) {
			console.error("저장 중 오류 발생:", error);
			alert("저장 중 오류가 발생했습니다.");
		} finally {
			setIsSaving(false);
		}
	};

	// 모달 바깥 클릭 핸들러
	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
			onClick={handleBackdropClick}
		>
			<div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
				{/* 모달 헤더 */}
				<div className="flex justify-between items-center border-b p-4">
					<h3 className="text-lg font-semibold">
						승차권 이미지 등록
					</h3>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700"
					>
						<XMarkIcon className="h-6 w-6" />
					</button>
				</div>

				{/* 모달 바디 */}
				<div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
					{/* 안내 메시지 */}
					<div
						className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded relative"
						role="alert"
					>
						<div className="flex">
							<div className="py-1">
								<svg
									className="fill-current h-6 w-6 text-blue-500 mr-4"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 20 20"
								>
									<path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
								</svg>
							</div>
							<div>
								<p className="font-bold">
									승차권 이미지를 직접 등록합니다.
								</p>
								<p className="text-sm">
									승차권 이미지를 추가하고 정보를 입력하는
									페이지 입니다.
								</p>
							</div>
						</div>
					</div>

					{/* 이미지 업로드 섹션 */}
					<div className="mb-6 border-b pb-6">
						<h4 className="text-lg font-medium mb-4">
							승차권 이미지 등록
						</h4>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* 이미지 업로드 영역 */}
							<div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center">
								{imagePreview ? (
									<div className="relative w-full">
										<img
											src={imagePreview}
											alt="승차권 이미지"
											className="max-h-64 object-contain mx-auto"
										/>
										<div className="flex justify-center mt-4">
											<button
												onClick={() => {
													const fileInput =
														document.getElementById(
															"file-upload"
														);
													if (fileInput)
														fileInput.click();
												}}
												className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
											>
												파일 찾아보기
											</button>
											<input
												id="file-upload"
												name="file-upload"
												type="file"
												accept="image/jpeg,image/png,image/jpg"
												className="sr-only"
												onChange={handleImageChange}
											/>
										</div>
									</div>
								) : (
									<div className="text-center">
										<svg
											className="mx-auto h-12 w-12 text-gray-400"
											stroke="currentColor"
											fill="none"
											viewBox="0 0 48 48"
										>
											<path
												d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										<div className="mt-6">
											<button
												onClick={() => {
													const fileInput =
														document.getElementById(
															"file-upload"
														);
													if (fileInput)
														fileInput.click();
												}}
												className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
											>
												파일 찾아보기
											</button>
											<input
												id="file-upload"
												name="file-upload"
												type="file"
												accept="image/jpeg,image/png,image/jpg"
												className="sr-only"
												onChange={handleImageChange}
											/>
										</div>
										<p className="text-xs text-gray-500 mt-3">
											PNG, JPG, JPEG 파일만 가능 (10MB
											이하)
										</p>
									</div>
								)}
							</div>

							{/* 티켓 타입 선택 영역 - 드롭다운 버튼 */}
							<div className="relative">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									승차권 타입 선택
								</label>
								
								{/* 현재 선택된 티켓 타입 및 드롭다운 버튼 */}
								<div 
									className="flex items-center justify-between w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer hover:bg-gray-50"
									onClick={() => setIsTicketTypeDropdownOpen(!isTicketTypeDropdownOpen)}
								>
									<span className="block truncate">{ticketType}</span>
									{isTicketTypeDropdownOpen ? (
										<ChevronUpIcon className="h-5 w-5 text-gray-400" />
									) : (
										<ChevronDownIcon className="h-5 w-5 text-gray-400" />
									)}
								</div>
								
								{/* 드롭다운 메뉴 */}
								{isTicketTypeDropdownOpen && (
									<div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
										{["일반승차권", "바우처", "홈티켓", "SRT", "특단"].map((type) => (
											<div
												key={type}
												className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${ticketType === type ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
												onClick={() => {
													handleTicketTypeChange({ target: { value: type } });
													setIsTicketTypeDropdownOpen(false);
												}}
											>
												{type}
											</div>
										))}
									</div>
								)}
								
								<p className="mt-2 text-sm text-gray-500">
									승차권 유형에 따라 적절한 타입을 선택해주세요. (모든 티켓에 일괄 적용됩니다)
								</p>
							</div>
						</div>
					</div>

					<div>
						{/* 열차 목록 탭 UI */}
						<div className="my-7">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-medium text-gray-700">
									열차 목록
								</h3>
								<button
									type="button"
									className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out transform hover:scale-105"
									onClick={handleAddTicket}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5 mr-1"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
											clipRule="evenodd"
										/>
									</svg>
									열차 추가
								</button>
							</div>
							<div
								className="flex flex-wrap gap-2 mb-6"
								role="tablist"
								aria-label="티켓 탭"
							>
								{ticketItems.map((ticket, index) => (
									<div key={index} className="relative group">
										<button
											className={`
											py-2 px-6 rounded-lg font-medium text-base transition-all duration-200
											${
												selectedTicketIndex === index
													? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
													: "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm"
											}`}
											onClick={() =>
												setSelectedTicketIndex(index)
											}
										>
											#{index + 1}
										</button>
										{ticketItems.length > 1 && (
											<button
												className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleRemoveTicket(index);
												}}
												title="열차 삭제"
											>
												<XMarkIcon className="h-4 w-4" />
											</button>
										)}
									</div>
								))}
							</div>
						</div>

						{/* 티켓 정보 테이블 - 현재 선택된 열차의 정보만 표시 */}
						<div className="overflow-x-auto">
							{/* <div className="flex items-center justify-between mb-4">
								<h4 className="text-lg font-medium">
									열차 #{selectedTicketIndex + 1} 정보
								</h4>
							</div> */}
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-52"
										>
											예약번호
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32"
										>
											출발일자
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											열차번호
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											출발역
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											도착역
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											출발시간
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											도착시간
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										>
											좌석정보
										</th>
										<th
											scope="col"
											className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
										/>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{ticketItems.length > 0 &&
										ticketItems[selectedTicketIndex] &&
										(() => {
											const ticket =
												ticketItems[
													selectedTicketIndex
												];
											const ticketIndex =
												selectedTicketIndex;

											// 날짜와 시간 분리
											let departureDate = "";
											let departureTime = "";
											let arrivalTime = "";

											if (ticket.departureTime) {
												const depDateTime = new Date(
													ticket.departureTime
												);

												// 한국 시간 기준으로 날짜를 가져오기 (+9시간)
												depDateTime.setHours(
													depDateTime.getHours() + 9
												);
												departureDate = depDateTime
													.toISOString()
													.split("T")[0];

												departureTime = depDateTime
													.toTimeString()
													.slice(0, 5);
											}

											if (ticket.arrivalTime) {
												const arrDateTime = new Date(
													ticket.arrivalTime
												);

												// 한국 시간 기준으로 날짜를 가져오기 (+9시간)
												arrDateTime.setHours(
													arrDateTime.getHours() + 9
												);

												arrivalTime = arrDateTime
													.toTimeString()
													.slice(0, 5);
											}

											return (
												<tr
													key={ticketIndex}
													className="hover:bg-gray-50"
												>
													{/* <td className="px-3 py-2 align-top">
														<button
															className="flex items-center justify-center rounded-full w-5 h-5 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 transition-colors duration-150 mt-1"
															onClick={() =>
																handleRemoveTicket(
																	ticketIndex
																)
															}
														>
															<MinusIcon className="h-5 w-5" />
														</button>
													</td> */}
													<td className="px-3 py-2 align-top">
														<input
															type="text"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																ticket.reservationNumber ||
																""
															}
															onChange={(e) =>
																handleTicketChange(
																	ticketIndex,
																	"reservationNumber",
																	e.target
																		.value
																)
															}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="date"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																departureDate
															}
															onChange={(e) => {
																const newDate =
																	e.target
																		.value;

																const depDateObj =
																	new Date(
																		newDate
																	);

																depDateObj.setHours(
																	departureTime.split(
																		":"
																	)[0]
																);
																depDateObj.setMinutes(
																	departureTime.split(
																		":"
																	)[1]
																);

																depDateObj.setHours(
																	depDateObj.getHours() -
																		9
																);
																const adjustedDepTime =
																	depDateObj
																		.toTimeString()
																		.slice(
																			0,
																			5
																		);

																handleTicketChange(
																	ticketIndex,
																	"departureTime",
																	`${newDate} ${adjustedDepTime}`
																);
															}}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="text"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																ticket.trainCode ||
																""
															}
															onChange={(e) =>
																handleTicketChange(
																	ticketIndex,
																	"trainCode",
																	e.target
																		.value
																)
															}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="text"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																ticket.departureStation
															}
															onChange={(e) =>
																handleTicketChange(
																	ticketIndex,
																	"departureStation",
																	e.target
																		.value
																)
															}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="text"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																ticket.arrivalStation
															}
															onChange={(e) =>
																handleTicketChange(
																	ticketIndex,
																	"arrivalStation",
																	e.target
																		.value
																)
															}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="time"
															className="border rounded px-2 py-1 w-full text-sm"
															value={
																departureTime
															}
															onChange={(e) => {
																const newTime =
																	e.target
																		.value;

																const depDateObj =
																	new Date(
																		departureDate
																	);
																depDateObj.setHours(
																	newTime.split(
																		":"
																	)[0]
																);
																depDateObj.setMinutes(
																	newTime.split(
																		":"
																	)[1]
																);

																// -9시간 적용
																depDateObj.setHours(
																	depDateObj.getHours() -
																		9
																);
																const adjustedDepTime =
																	depDateObj
																		.toTimeString()
																		.slice(
																			0,
																			5
																		);

																handleTicketChange(
																	ticketIndex,
																	"departureTime",
																	`${departureDate} ${adjustedDepTime}`
																);
															}}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<input
															type="time"
															className="border rounded px-2 py-1 w-full text-sm"
															value={arrivalTime}
															onChange={(e) => {
																const newTime =
																	e.target
																		.value;

																const depDateObj =
																	new Date(
																		departureDate
																	);
																depDateObj.setHours(
																	newTime.split(
																		":"
																	)[0]
																);
																depDateObj.setMinutes(
																	newTime.split(
																		":"
																	)[1]
																);

																// -9시간 적용
																depDateObj.setHours(
																	depDateObj.getHours() -
																		9
																);
																const adjustedDepTime =
																	depDateObj
																		.toTimeString()
																		.slice(
																			0,
																			5
																		);

																handleTicketChange(
																	ticketIndex,
																	"arrivalTime",
																	`${departureDate} ${adjustedDepTime}`
																);
															}}
														/>
													</td>
													<td className="px-3 py-2 align-top">
														<div className="flex flex-col space-y-2">
															{ticket.seatInfoList &&
																ticket.seatInfoList.map(
																	(
																		seat,
																		seatIndex
																	) => {
																		const rangeResult =
																			parseSeatRange(
																				seat.seatCode
																			);

																		return (
																			<div
																				key={
																					seatIndex
																				}
																				className="flex items-center space-x-1"
																			>
																				<div className="flex-1 flex space-x-1">
																					<input
																						type="text"
																						className="border rounded px-2 py-1 w-1/2 text-sm"
																						placeholder="호차"
																						value={
																							seat.trainCarNumber ||
																							""
																						}
																						onChange={(
																							e
																						) => {
																							// 숫자만 입력 가능하게 처리
																							const numericValue =
																								e.target.value.replace(
																									/[^0-9]/g,
																									""
																								);
																							handleSeatChange(
																								ticketIndex,
																								seatIndex,
																								"trainCarNumber",
																								numericValue
																							);
																						}}
																						onBlur={(
																							e
																						) => {
																							// focus out 시 호차 추가
																							if (
																								e
																									.target
																									.value &&
																								!e.target.value.includes(
																									"호차"
																								)
																							) {
																								handleSeatChange(
																									ticketIndex,
																									seatIndex,
																									"trainCarNumber",
																									`${e.target.value}호차`
																								);
																							}
																						}}
																					/>
																					<div className="w-1/2">
																						<input
																							type="text"
																							className="border rounded px-2 py-1 w-full text-sm"
																							placeholder="좌석"
																							value={
																								seat.seatCode ||
																								""
																							}
																							onChange={(
																								e
																							) =>
																								handleSeatChange(
																									ticketIndex,
																									seatIndex,
																									"seatCode",
																									e
																										.target
																										.value
																								)
																							}
																							onKeyDown={(
																								e
																							) => {
																								if (
																									e.key !==
																									"Enter"
																								) {
																									return;
																								}

																								const result =
																									parseSeatRange(
																										e
																											.target
																											.value
																									);
																								if (
																									!result.isRange ||
																									result.error
																								) {
																									return;
																								}

																								e.preventDefault();
																								applySeatRange(
																									ticketIndex,
																									seatIndex,
																									e
																										.target
																										.value
																								);
																							}}
																						/>
																						<p
																							className={`mt-1 min-h-[14px] text-xs leading-tight truncate ${
																								rangeResult.isRange
																									? rangeResult.error
																										? "text-red-500"
																										: "text-gray-400"
																									: "invisible"
																							}`}
																						>
																							{rangeResult.isRange
																								? rangeResult.error
																									? rangeResult.error
																									: `Enter로 범위 좌석 생성 (${rangeResult.count}석)`
																								: "\u00A0"}
																						</p>
																					</div>
																				</div>
																				<button
																					className="flex items-center justify-center rounded-full w-5 h-5 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 transition-colors duration-150"
																					onClick={() =>
																						handleRemoveSeat(
																							ticketIndex,
																							seatIndex
																						)
																					}
																					title="좌석 삭제"
																				>
																					<MinusIcon className="h-3 w-3" />
																				</button>
																			</div>
																		);
																	}
																)}
														</div>
													</td>
													<td className="px-3 py-2 align-top">
														<button
															className="flex items-center justify-center space-x-1 bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 py-1.5 px-3 rounded text-xs transition-colors duration-150 border border-blue-200 whitespace-nowrap"
															onClick={() =>
																handleAddSeat(
																	ticketIndex
																)
															}
														>
															<PlusIcon className="h-3 w-3" />
															<span>
																좌석추가
															</span>
														</button>
													</td>
												</tr>
											);
										})()}
								</tbody>
							</table>
						</div>
					</div>
				</div>

				{/* 모달 푸터 */}
				<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
					<button
						type="button"
						className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
						onClick={handleSave}
						disabled={isSaving}
					>
						{isSaving ? "저장 중..." : "저장"}
					</button>
					<button
						type="button"
						className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
						onClick={onClose}
					>
						취소
					</button>
				</div>
			</div>
		</div>
	);
};

export default RegisterDetailWithImageModal;
